import { type PagyMeta, PagyPagination } from "@/components/pagy-pagination"
import { Avatar, AvatarFallback, AvatarGroup, AvatarGroupCount, AvatarImage } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import AppLayout from "@/layouts/AppLayout"
import { Head, router, usePage } from "@inertiajs/react"
import { IconDots, IconEye, IconPencil, IconPlus, IconSearch, IconTrash } from "@tabler/icons-react"
import { Fragment, useState } from "react"

interface User {
  id: number
  name: string
  avatar_url: string | null
}

interface SeniorityLevel {
  id: number
  level: number
  title: string
  keyword: string
  level_group: string
  level_group_emoji: string
  pl_incremental: string
  monthly_6h: string
  irpf_6h: string
  monthly_8h: string
  irpf_8h: string
  active: boolean
  users: User[]
  can_be_deleted: boolean
}

interface LevelGroup {
  name: string
  emoji: string
  levels: SeniorityLevel[]
}

interface StatusCounts {
  all: number
  active: number
  inactive: number
}

interface Props {
  [key: string]: unknown
  level_groups: LevelGroup[]
  pagy: PagyMeta
  status_counts: StatusCounts
  filters: { search?: string; active?: string }
  auth: { is_admin: boolean }
}

// Keep in step with the header row below; the group separator spans all of them.
const COLUMN_COUNT = 9

// What the serializer sends for a figure that isn't set.
const BLANK = "—"

// The IRPF slot is a fixed width and is always reserved, even when empty. Without
// it the monthly figures drift: a row carrying an IRPF value pushes its amount
// left while a row without one sits flush against the column edge.
const IRPF_SLOT = "w-20 shrink-0 text-left"

// The monthly figure with its IRPF alongside, so the 6-hour and 8-hour numbers
// read as one thing each instead of four lookalike columns. Levels above
// SemiSenior carry no IRPF and show the monthly figure alone.
function MonthlyPay({ monthly, irpf }: { monthly: string; irpf: string }) {
  return (
    <div className="flex items-baseline justify-end gap-2">
      <span>{monthly}</span>
      <span className={`${IRPF_SLOT} text-muted-foreground text-xs`}>{irpf === BLANK ? "" : `IRPF ${irpf}`}</span>
    </div>
  )
}

// Reserves the same slot so the label lands over the monthly figures rather than
// over the IRPF text.
function MonthlyPayHeader({ label }: { label: string }) {
  return (
    <div className="flex items-baseline justify-end gap-2">
      <span>{label}</span>
      <span className={IRPF_SLOT} aria-hidden="true" />
    </div>
  )
}

export default function AdminSeniorityLevelsIndex() {
  const { level_groups, pagy, status_counts, filters, auth } = usePage<Props>().props

  const [search, setSearch] = useState(filters.search || "")

  function handleSearch(e: React.FormEvent) {
    e.preventDefault()
    router.get(
      "/admin/seniority_levels",
      {
        search: search || undefined,
        active: filters.active || undefined,
      },
      { preserveState: true },
    )
  }

  function handleFilter(active?: string) {
    router.get(
      "/admin/seniority_levels",
      {
        search: filters.search || undefined,
        active,
      },
      { preserveState: true },
    )
  }

  function handleDelete(id: number) {
    if (confirm("Are you sure you want to delete this seniority level?")) {
      router.delete(`/admin/seniority_levels/${id}`)
    }
  }

  return (
    <>
      <Head title="Seniority Levels - Operations Center" />
      <div className="flex flex-1 flex-col gap-4 p-4 lg:p-6">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/">Home</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Seniority Levels</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold">Seniority Levels</h1>
          {auth.is_admin && (
            <Button size="sm" asChild>
              <a href="/admin/seniority_levels/new">
                <IconPlus className="mr-1 h-4 w-4" />
                New Level
              </a>
            </Button>
          )}
        </div>

        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <form onSubmit={handleSearch} className="flex gap-2 sm:max-w-sm">
            <div className="relative flex-1">
              <IconSearch className="text-muted-foreground absolute top-1/2 left-2.5 h-4 w-4 -translate-y-1/2" />
              <Input
                placeholder="Search levels..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <Button type="submit" variant="outline">
              Search
            </Button>
          </form>

          <div className="flex items-center gap-2">
            {(
              [
                { label: "All", value: undefined, count: status_counts.all },
                { label: "Active", value: "true", count: status_counts.active },
                { label: "Inactive", value: "false", count: status_counts.inactive },
              ] as const
            ).map((filter) => (
              <Button
                key={filter.label}
                variant={filters.active === filter.value ? "default" : "outline"}
                size="sm"
                onClick={() => handleFilter(filter.value)}
              >
                {filter.label} ({filter.count})
              </Button>
            ))}
          </div>
        </div>

        {/*
          One table for every group rather than one per group: separate tables
          size their columns independently, so the money columns did not line up
          from one group to the next.
        */}
        {level_groups.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-16">Level</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Keyword</TableHead>
                <TableHead className="text-right">P/L Inc.</TableHead>
                <TableHead>
                  <MonthlyPayHeader label="6 Hours" />
                </TableHead>
                <TableHead>
                  <MonthlyPayHeader label="8 Hours" />
                </TableHead>
                <TableHead>Employees</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-10">
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {level_groups.map((group) => (
                <Fragment key={group.name}>
                  <TableRow className="hover:bg-transparent">
                    <TableCell
                      colSpan={COLUMN_COUNT}
                      className="text-muted-foreground bg-muted/50 text-sm font-semibold uppercase"
                    >
                      {group.emoji} {group.name}
                    </TableCell>
                  </TableRow>
                  {group.levels.map((level) => (
                    <TableRow key={level.id}>
                      <TableCell>
                        <span className="bg-primary/10 text-primary inline-flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold">
                          {level.level}
                        </span>
                      </TableCell>
                      <TableCell className="font-medium">
                        <a href={`/admin/seniority_levels/${level.id}`} className="hover:underline">
                          {level.title}
                        </a>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">{level.keyword}</Badge>
                      </TableCell>
                      <TableCell className="text-right">{level.pl_incremental}</TableCell>
                      <TableCell>
                        <MonthlyPay monthly={level.monthly_6h} irpf={level.irpf_6h} />
                      </TableCell>
                      <TableCell>
                        <MonthlyPay monthly={level.monthly_8h} irpf={level.irpf_8h} />
                      </TableCell>
                      <TableCell>
                        {level.users.length > 0 ? (
                          <AvatarGroup>
                            {level.users.slice(0, 4).map((user) => (
                              <Avatar key={user.id} size="sm" title={user.name}>
                                {user.avatar_url ? <AvatarImage src={user.avatar_url} alt={user.name} /> : null}
                                <AvatarFallback>{user.name.charAt(0).toUpperCase()}</AvatarFallback>
                              </Avatar>
                            ))}
                            {level.users.length > 4 && <AvatarGroupCount>+{level.users.length - 4}</AvatarGroupCount>}
                          </AvatarGroup>
                        ) : (
                          <span className="text-muted-foreground text-sm">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={level.active ? "default" : "destructive"}>
                          {level.active ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <IconDots className="h-4 w-4" />
                              <span className="sr-only">Actions</span>
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem asChild>
                              <a href={`/admin/seniority_levels/${level.id}`}>
                                <IconEye className="mr-2 h-4 w-4" />
                                View
                              </a>
                            </DropdownMenuItem>
                            <DropdownMenuItem asChild>
                              <a href={`/admin/seniority_levels/${level.id}/edit`}>
                                <IconPencil className="mr-2 h-4 w-4" />
                                Edit
                              </a>
                            </DropdownMenuItem>
                            {level.can_be_deleted && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem variant="destructive" onClick={() => handleDelete(level.id)}>
                                  <IconTrash className="mr-2 h-4 w-4" />
                                  Delete
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </Fragment>
              ))}
            </TableBody>
          </Table>
        )}

        {level_groups.length === 0 && <p className="text-muted-foreground text-center">No seniority levels found.</p>}

        <PagyPagination pagy={pagy} />
      </div>
    </>
  )
}

AdminSeniorityLevelsIndex.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
